// File: src/components/research/ResearchDetailsContent.tsx
// New component to encapsulate the detailed rendering of a single ResearchIteration.

import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    Link, // ✅ ИЗМЕНЕНИЕ: Импортируем компонент Link
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import {ExpandMore as ExpandMoreIcon} from '@mui/icons-material';
import {ResearchIteration} from '../../types';

interface ResearchDetailsContentProps {
    researchResult: ResearchIteration;
}

const ResearchDetailsContent: React.FC<ResearchDetailsContentProps> = ({researchResult}) => {
    return (
        <Box>
            {researchResult.similar_products?.products && researchResult.similar_products.products.length > 0 && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>🛍️ Similar Products ({researchResult.similar_products.products.length})</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Brand</TableCell>
                                        <TableCell>Price Range</TableCell>
                                        <TableCell>Similarity</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* ✅ ИЗМЕНЕНИЕ: Добавлена логика для отображения ссылки */}
                                    {researchResult.similar_products.products.map((p, i) => (
                                        <TableRow key={i}>
                                            <TableCell>
                                                {p.url ? (
                                                    <Link href={p.url} target="_blank" rel="noopener noreferrer">
                                                        {p.name || 'N/A'}
                                                    </Link>
                                                ) : (
                                                    p.name || 'N/A'
                                                )}
                                            </TableCell>
                                            <TableCell>{p.brand || 'N/A'}</TableCell>
                                            <TableCell>{p.price_range || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={p.similarity ? `${(p.similarity * 100).toFixed(0)}%` : 'N/A'}
                                                    color={p.similarity > 0.8 ? 'success' : 'warning'}
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            )}

            {researchResult.competitor_analysis && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>🏆 Competitor Analysis</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{display: 'grid', gap: 2}}>
                            {researchResult.competitor_analysis.top_brands && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Top Brands:</Typography>
                                    {researchResult.competitor_analysis.top_brands.map((brand: string, i: number) => (
                                        <Chip key={i} label={brand} color="primary" sx={{mr: 1, mb: 1}}/>
                                    ))}
                                </Box>
                            )}

                            <Typography>
                                <strong>Price Range:</strong> {researchResult.competitor_analysis.price_range}
                            </Typography>

                            <Typography>
                                <strong>Rating Threshold:</strong> {researchResult.competitor_analysis.rating_threshold}
                            </Typography>

                            {researchResult.competitor_analysis.successful_keywords && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Successful Keywords:</Typography>
                                    {researchResult.competitor_analysis.successful_keywords.map((keyword: string, i: number) => (
                                        <Chip key={i} label={keyword} variant="outlined" sx={{mr: 1, mb: 1}}/>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {researchResult.target_audience && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>👥 Target Audience</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3}}>
                            {researchResult.target_audience.primary && (
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" color="primary" gutterBottom>
                                            Primary Audience
                                        </Typography>
                                        <Typography variant="body2" sx={{mb: 1}}>
                                            <strong>Age:</strong> {researchResult.target_audience.primary.age || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{mb: 1}}>
                                            <strong>Gender:</strong> {researchResult.target_audience.primary.gender || 'N/A'}
                                        </Typography>
                                        {researchResult.target_audience.primary.interests && researchResult.target_audience.primary.interests.length > 0 && (
                                            <Box sx={{mt: 1}}>
                                                <Typography variant="body2" sx={{mb: 1}}><strong>Interests:</strong></Typography>
                                                {researchResult.target_audience.primary.interests.map((interest: string, i: number) => (
                                                    <Chip key={i} label={interest} size="small" sx={{mr: 0.5, mb: 0.5}}/>
                                                ))}
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {researchResult.target_audience.secondary && (
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" color="secondary" gutterBottom>
                                            Secondary Audience
                                        </Typography>
                                        <Typography variant="body2" sx={{mb: 1}}>
                                            <strong>Age:</strong> {researchResult.target_audience.secondary.age || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" sx={{mb: 1}}>
                                            <strong>Gender:</strong> {researchResult.target_audience.secondary.gender || 'N/A'}
                                        </Typography>
                                        {researchResult.target_audience.secondary.interests && researchResult.target_audience.secondary.interests.length > 0 && (
                                            <Box sx={{mt: 1}}>
                                                <Typography variant="body2" sx={{mb: 1}}><strong>Interests:</strong></Typography>
                                                {researchResult.target_audience.secondary.interests.map((interest: string, i: number) => (
                                                    <Chip key={i} label={interest} size="small" color="secondary" sx={{mr: 0.5, mb: 0.5}}/>
                                                ))}
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {researchResult.seo_keywords && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>🔍 SEO Keywords</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{display: 'grid', gap: 2}}>
                            {researchResult.seo_keywords.primary_keywords && researchResult.seo_keywords.primary_keywords.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Primary Keywords:</Typography>
                                    {researchResult.seo_keywords.primary_keywords.map((keyword: string, i: number) => (
                                        <Chip key={i} label={keyword} color="primary" sx={{mr: 1, mb: 1}}/>
                                    ))}
                                </Box>
                            )}

                            {researchResult.seo_keywords.content_keywords && researchResult.seo_keywords.content_keywords.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Content Keywords:</Typography>
                                    {researchResult.seo_keywords.content_keywords.map((keyword: string, i: number) => (
                                        <Chip key={i} label={keyword} color="secondary" variant="outlined" sx={{mr: 1, mb: 1}}/>
                                    ))}
                                </Box>
                            )}

                            {researchResult.seo_keywords.marketplace_keywords && researchResult.seo_keywords.marketplace_keywords.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Marketplace Keywords:</Typography>
                                    {researchResult.seo_keywords.marketplace_keywords.map((keyword: string, i: number) => (
                                        <Chip key={i} label={keyword} size="small" sx={{mr: 1, mb: 1}}/>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {researchResult.content_ideas && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>✍️ Content Ideas</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{display: 'grid', gap: 3}}>
                            {researchResult.content_ideas.title_suggestions && researchResult.content_ideas.title_suggestions.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Title Suggestions:</Typography>
                                    {researchResult.content_ideas.title_suggestions.map((title: string, i: number) => (
                                        <Alert key={i} severity="info" sx={{mb: 1}}>
                                            {title}
                                        </Alert>
                                    ))}
                                </Box>
                            )}

                            {researchResult.content_ideas.description_ideas && researchResult.content_ideas.description_ideas.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Description Ideas:</Typography>
                                    <Paper variant="outlined" sx={{p: 2}}>
                                        <ul style={{margin: 0, paddingLeft: '20px'}}>
                                            {researchResult.content_ideas.description_ideas.map((idea: string, i: number) => (
                                                <li key={i} style={{marginBottom: '8px'}}>{idea}</li>
                                            ))}
                                        </ul>
                                    </Paper>
                                </Box>
                            )}

                            {researchResult.content_ideas.photo_guidelines && researchResult.content_ideas.photo_guidelines.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Photo Guidelines:</Typography>
                                    <Paper variant="outlined" sx={{p: 2, bgcolor: 'success.light', color: 'success.contrastText'}}>
                                        <ul style={{margin: 0, paddingLeft: '20px'}}>
                                            {researchResult.content_ideas.photo_guidelines.map((guideline: string, i: number) => (
                                                <li key={i} style={{marginBottom: '8px'}}>{guideline}</li>
                                            ))}
                                        </ul>
                                    </Paper>
                                </Box>
                            )}

                            {researchResult.content_ideas.rich_content && researchResult.content_ideas.rich_content.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Rich Content Ideas:</Typography>
                                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                        {researchResult.content_ideas.rich_content.map((content: string, i: number) => (
                                            <Chip key={i} label={content} variant="outlined" color="info"/>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {researchResult.market_insights && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>📈 Market Insights</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{display: 'grid', gap: 2}}>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>Seasonal Trends:</Typography>
                                <Paper variant="outlined" sx={{p: 2}}>
                                <pre style={{whiteSpace: 'pre-wrap', fontFamily: 'inherit'}}>
                                    {JSON.stringify(researchResult.market_insights.seasonal_trends, null, 2) || 'No data'}
                                </pre>
                                </Paper>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>Regional Preferences:</Typography>
                                <Paper variant="outlined" sx={{p: 2}}>
                                <pre style={{whiteSpace: 'pre-wrap', fontFamily: 'inherit'}}>
                                    {JSON.stringify(researchResult.market_insights.regional_preferences, null, 2) || 'No data'}
                                </pre>
                                </Paper>
                            </Box>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {researchResult.product_strengths && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>💪 Product Strengths</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {researchResult.product_strengths.top_3 && Array.isArray(researchResult.product_strengths.top_3) && researchResult.product_strengths.top_3.length > 0 ? (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>Top 3 Strengths:</Typography>
                                <Box sx={{display: 'grid', gap: 1}}>
                                    {researchResult.product_strengths.top_3.map((strength: string, i: number) => (
                                        <Paper key={i} variant="outlined" sx={{p: 2, display: 'flex', alignItems: 'center'}}>
                                            <Typography variant="h6" color="primary" sx={{mr: 2}}>
                                                #{i + 1}
                                            </Typography>
                                            <Typography variant="body1">
                                                {strength}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No specific product strengths identified
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            {researchResult.product_weaknesses && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>👎 Product Weaknesses</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {researchResult.product_weaknesses.top_3 && Array.isArray(researchResult.product_weaknesses.top_3) && researchResult.product_weaknesses.top_3.length > 0 ? (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>Top 3 Weaknesses:</Typography>
                                <Box sx={{display: 'grid', gap: 1}}>
                                    {researchResult.product_weaknesses.top_3.map((weakness: string, i: number) => (
                                        <Paper key={i} variant="outlined" sx={{p: 2, display: 'flex', alignItems: 'center'}}>
                                            <Typography variant="h6" color="error" sx={{mr: 2}}>
                                                #{i + 1}
                                            </Typography>
                                            <Typography variant="body1">
                                                {weakness}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No specific product weaknesses identified
                            </Typography>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}
            {researchResult.marketplace_insights && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>🛒 Marketplace Insights</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{display: 'grid', gap: 2}}>
                            {researchResult.marketplace_insights.naming_structure && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Naming Structure:</Typography>
                                    <Alert severity="info">
                                        {researchResult.marketplace_insights.naming_structure}
                                    </Alert>
                                </Box>
                            )}

                            {researchResult.marketplace_insights.pricing_strategy && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Pricing Strategy:</Typography>
                                    <Alert severity="warning">
                                        {researchResult.marketplace_insights.pricing_strategy}
                                    </Alert>
                                </Box>
                            )}

                            {researchResult.marketplace_insights.recommended_content && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Recommended Content:</Typography>
                                    <Paper variant="outlined" sx={{p: 2}}>
                                        <Typography variant="body2">
                                            {researchResult.marketplace_insights.recommended_content}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            {researchResult.product_description && (researchResult.product_description.title || researchResult.product_description.body) && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography>📝 Product Description</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            {researchResult.product_description.title && (
                                <Typography variant="h6" component="h3" gutterBottom>
                                    {researchResult.product_description.title}
                                </Typography>
                            )}
                            {researchResult.product_description.body && (
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {researchResult.product_description.body}
                                </Typography>
                            )}
                        </Paper>
                    </AccordionDetails>
                </Accordion>
            )}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                    <Typography>Debug Info (RAW)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography variant="overline">Prompt</Typography>
                    <Paper variant="outlined"
                           sx={{p: 1, mb: 2, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto'}}>
                        {researchResult.ai_raw_prompt || 'Not saved.'}
                    </Paper>
                    <Typography variant="overline">Response</Typography>
                    <Paper variant="outlined"
                           sx={{p: 1, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto'}}>
                        {researchResult.ai_raw_response || 'Not available.'}
                    </Paper>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default ResearchDetailsContent;